import { promises as fs } from "node:fs";
import path from "node:path";

/** One assistant response's token usage, extracted from a session jsonl line. */
export interface UsageRecord {
  /** Epoch milliseconds of the message. */
  timestamp: number;
  /** Session id (from the session header line, fallback: file name). */
  sessionId: string;
  /** Working directory of the session (from the session header line). */
  cwd: string;
  provider: string;
  model: string;
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
  reasoning: number;
  totalTokens: number;
  /** Total cost in USD, as reported by the session log. */
  cost: number;
}

interface FileCacheEntry {
  mtimeMs: number;
  size: number;
  records: UsageRecord[];
}

export interface ScanResult {
  records: UsageRecord[];
  fileCount: number;
  /** Files whose content was actually parsed in this scan. */
  parsedFiles: number;
  /** Milliseconds the scan took. */
  elapsedMs: number;
}

async function listJsonlFiles(dir: string): Promise<string[]> {
  const out: string[] = [];
  let entries;
  try {
    entries = await fs.readdir(dir, { withFileTypes: true });
  } catch {
    return out;
  }
  const jsonlNames = new Set(
    entries
      .filter((e) => e.isFile() && e.name.endsWith(".jsonl"))
      .map((e) => e.name),
  );
  for (const entry of entries) {
    const full = path.join(dir, entry.name);
    if (entry.isDirectory()) {
      // Nested subagent sessions live at `<session-stem>/<runId>/run-N/*.jsonl`
      // next to their parent's `<session-stem>.jsonl`. Their usage is already
      // accounted from the parent's subagent toolResult entries, so skip these
      // directories to avoid double counting.
      if (jsonlNames.has(`${entry.name}.jsonl`)) continue;
      out.push(...(await listJsonlFiles(full)));
    } else if (entry.isFile() && entry.name.endsWith(".jsonl")) {
      out.push(full);
    }
  }
  return out;
}

function toNumber(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function parseTimestamp(raw: unknown, messageTimestamp: unknown): number {
  if (typeof raw === "string") {
    const parsed = Date.parse(raw);
    if (!Number.isNaN(parsed)) return parsed;
  }
  if (typeof messageTimestamp === "number" && Number.isFinite(messageTimestamp)) {
    return messageTimestamp;
  }
  return 0;
}

/** Cost may be an object (`{ total }`, main messages) or a bare number (tool details). */
function parseCost(raw: unknown): number {
  if (typeof raw === "number") return toNumber(raw);
  if (raw && typeof raw === "object") {
    return toNumber((raw as Record<string, unknown>).total);
  }
  return 0;
}

/**
 * Split a subagent model string like "opencode-go/deepseek-v4-flash:medium"
 * into provider + model, dropping the `:thinking` suffix.
 */
function parseModelString(raw: unknown): { provider: string; model: string } {
  if (typeof raw !== "string" || !raw) return { provider: "unknown", model: "unknown" };
  const slash = raw.indexOf("/");
  const provider = slash > 0 ? raw.slice(0, slash) : "unknown";
  let model = slash > 0 ? raw.slice(slash + 1) : raw;
  const colon = model.indexOf(":");
  if (colon > 0) model = model.slice(0, colon);
  return { provider, model: model || "unknown" };
}

interface SubagentUsageShape {
  input?: unknown;
  output?: unknown;
  cacheRead?: unknown;
  cacheWrite?: unknown;
  reasoning?: unknown;
  totalTokens?: unknown;
  cost?: unknown;
}

function buildSubagentRecord(
  usage: SubagentUsageShape,
  base: { timestamp: number; sessionId: string; cwd: string; provider: string; model: string },
): UsageRecord {
  const input = toNumber(usage.input);
  const output = toNumber(usage.output);
  const cacheRead = toNumber(usage.cacheRead);
  const cacheWrite = toNumber(usage.cacheWrite);
  return {
    ...base,
    input,
    output,
    cacheRead,
    cacheWrite,
    reasoning: toNumber(usage.reasoning),
    totalTokens: toNumber(usage.totalTokens) || input + output + cacheRead + cacheWrite,
    cost: parseCost(usage.cost),
  };
}

/**
 * Extract subagent token usage from a toolResult message. Both subagent tools
 * report aggregated child usage in `message.details`:
 *   - subagent:        details.runId + details.results[] (per-child usage/model),
 *                      falling back to details.totalChildUsage
 *   - trellis_subagent: details.runs[] (per-run usage/model/agent/id)
 */
function subagentRecordsFromToolResult(
  message: Record<string, unknown>,
  base: { timestamp: number; cwd: string },
): UsageRecord[] {
  const details = message.details;
  if (!details || typeof details !== "object") return [];
  const d = details as Record<string, unknown>;
  const toolName = message.toolName;
  const records: UsageRecord[] = [];

  if (toolName === "subagent") {
    const runId = typeof d.runId === "string" ? d.runId : "";
    const results = Array.isArray(d.results) ? d.results : [];
    results.forEach((result, index) => {
      if (!result || typeof result !== "object") return;
      const r = result as Record<string, unknown>;
      if (!r.usage || typeof r.usage !== "object") return;
      const agent = typeof r.agent === "string" && r.agent ? r.agent : "subagent";
      const sessionId = [runId, agent, String(index)].filter(Boolean).join("_");
      if (!sessionId) return;
      records.push(
        buildSubagentRecord(r.usage as SubagentUsageShape, {
          ...base,
          sessionId,
          ...parseModelString(r.model),
        }),
      );
    });
    // Fallback when per-child results are unavailable (e.g. older logs).
    if (records.length === 0 && d.totalChildUsage && typeof d.totalChildUsage === "object") {
      const sessionId = runId ? `${runId}_subagent` : "";
      if (sessionId) {
        records.push(
          buildSubagentRecord(d.totalChildUsage as SubagentUsageShape, {
            ...base,
            sessionId,
            provider: "unknown",
            model: "unknown",
          }),
        );
      }
    }
    return records;
  }

  if (toolName === "trellis_subagent") {
    const runs = Array.isArray(d.runs) ? d.runs : [];
    for (const run of runs) {
      if (!run || typeof run !== "object") continue;
      const r = run as Record<string, unknown>;
      if (!r.usage || typeof r.usage !== "object") continue;
      const agent = typeof r.agent === "string" && r.agent ? r.agent : "trellis";
      const id = typeof r.id === "string" ? r.id : "";
      const sessionId = ["trellis", agent, id].filter(Boolean).join("_");
      records.push(
        buildSubagentRecord(r.usage as SubagentUsageShape, {
          ...base,
          sessionId,
          provider: "unknown",
          model: typeof r.model === "string" && r.model ? r.model : "unknown",
        }),
      );
    }
    return records;
  }

  return records;
}

/**
 * Parse one session jsonl file into usage records.
 * Two sources contribute:
 *   - assistant messages carrying a `usage` object (per-request billing data)
 *   - subagent / trellis_subagent toolResult messages, whose `details` carry
 *     the aggregated token usage of the spawned child agents
 */
export function parseSessionFile(content: string, fallbackSessionId: string): UsageRecord[] {
  const records: UsageRecord[] = [];
  const seenMessageIds = new Set<string>();
  let sessionId = fallbackSessionId;
  let cwd = "";

  for (const line of content.split("\n")) {
    if (!line || line[0] !== "{") continue;
    // Cheap pre-filter: usage only appears on assistant message lines and on
    // subagent toolResult lines (inside `details`, as "usage" or
    // "totalChildUsage").
    if (!line.includes('"usage"') && !line.includes('"totalChildUsage"')) {
      // Main sessions store cwd/id in a `type: "session"` header line.
      if (line.includes('"type":"session"')) {
        try {
          const header = JSON.parse(line) as { id?: unknown; cwd?: unknown };
          if (typeof header.id === "string" && header.id) sessionId = header.id;
          if (typeof header.cwd === "string") cwd = header.cwd;
        } catch {
          // ignore malformed header lines
        }
      }
      continue;
    }

    let event: {
      type?: unknown;
      id?: unknown;
      timestamp?: unknown;
      message?: Record<string, unknown> & {
        role?: unknown;
        provider?: unknown;
        model?: unknown;
        timestamp?: unknown;
        usage?: Record<string, unknown>;
      };
    };
    try {
      event = JSON.parse(line);
    } catch {
      continue;
    }
    if (event.type !== "message") continue;

    const message = event.message;
    if (!message) continue;

    // De-dup by message id; skip dedup when no id is present.
    const messageId = typeof event.id === "string" && event.id ? event.id : "";
    if (messageId) {
      if (seenMessageIds.has(messageId)) continue;
      seenMessageIds.add(messageId);
    }

    const timestamp = parseTimestamp(event.timestamp, message.timestamp);
    if (!timestamp) continue;

    if (message.role === "toolResult") {
      const toolName = message.toolName;
      if (toolName === "subagent" || toolName === "trellis_subagent") {
        records.push(...subagentRecordsFromToolResult(message, { timestamp, cwd }));
      }
      continue;
    }

    if (message.role !== "assistant" || !message.usage) continue;

    const usage = message.usage;
    const input = toNumber(usage.input);
    const output = toNumber(usage.output);
    const cacheRead = toNumber(usage.cacheRead);
    const cacheWrite = toNumber(usage.cacheWrite);

    records.push({
      timestamp,
      sessionId,
      cwd,
      provider: typeof message.provider === "string" ? message.provider : "unknown",
      model: typeof message.model === "string" ? message.model : "unknown",
      input,
      output,
      cacheRead,
      cacheWrite,
      reasoning: toNumber(usage.reasoning),
      totalTokens: toNumber(usage.totalTokens) || input + output + cacheRead + cacheWrite,
      cost: parseCost(usage.cost),
    });
  }
  return records;
}

/**
 * Incremental scanner for the sessions directory. Caches parsed files by
 * (mtimeMs, size) so repeated scans only re-read changed files.
 */
export class SessionStore {
  readonly dir: string;
  private cache = new Map<string, FileCacheEntry>();

  constructor(dir: string) {
    this.dir = dir;
  }

  async scan(): Promise<ScanResult> {
    const started = Date.now();
    const files = await listJsonlFiles(this.dir);
    const alive = new Set(files);
    for (const cachedPath of this.cache.keys()) {
      if (!alive.has(cachedPath)) this.cache.delete(cachedPath);
    }

    let parsedFiles = 0;
    await Promise.all(
      files.map(async (file) => {
        let stat;
        try {
          stat = await fs.stat(file);
        } catch {
          return;
        }
        const cached = this.cache.get(file);
        if (cached && cached.mtimeMs === stat.mtimeMs && cached.size === stat.size) {
          return;
        }
        let content: string;
        try {
          content = await fs.readFile(file, "utf8");
        } catch {
          return;
        }
        const fallbackSessionId = path.basename(file, ".jsonl");
        const records = parseSessionFile(content, fallbackSessionId);
        this.cache.set(file, { mtimeMs: stat.mtimeMs, size: stat.size, records });
        parsedFiles += 1;
      }),
    );

    const records: UsageRecord[] = [];
    for (const entry of this.cache.values()) records.push(...entry.records);
    return {
      records,
      fileCount: files.length,
      parsedFiles,
      elapsedMs: Date.now() - started,
    };
  }
}
